import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Calendar, Clock, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-8 h-12 w-32 animate-pulse rounded-lg bg-muted/30" />
          <div className="mb-6 h-96 animate-pulse rounded-xl bg-muted/30" />
          <div className="mb-4 h-12 animate-pulse rounded-lg bg-muted/30" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} data-index={i} className="h-4 animate-pulse rounded bg-muted/30" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="mx-4 max-w-md border border-border shadow-soft">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">Post not found or failed to load.</p>
            <Link to="/">
              <Button className="bg-primary text-primary-foreground hover:bg-primary-hover">
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Align Physiotherapy";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const authorInitials = authorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const postDate = post.date
    ? new Date(post.date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <div className="min-h-screen bg-background">
      <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <Link to="/">
          <Button
            variant="ghost"
            className="mb-8 -ml-2 gap-2 text-primary hover:bg-primary/10 hover:text-primary transition-all duration-[var(--duration-fast)]"
          >
            <ArrowLeft className="size-4" />
            Back to Home
          </Button>
        </Link>

        {post.featuredImage && (
          <div className="mb-8 overflow-hidden rounded-xl shadow-medium">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-96 w-full object-cover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:scale-105"
            />
          </div>
        )}

        <header className="mb-8">
          <h1 className="mb-6 font-display text-4xl font-bold leading-[1.2] tracking-wide text-foreground md:text-5xl">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="size-10 border border-border">
                {authorAvatar ? (
                  <AvatarImage src={authorAvatar} alt={authorName} />
                ) : (
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {authorInitials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex items-center gap-1.5">
                <User className="size-4" />
                <span className="font-medium text-foreground">{authorName}</span>
              </div>
            </div>

            {postDate && (
              <div className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                <time dateTime={post.date}>{postDate}</time>
              </div>
            )}

            <div className="flex items-center gap-1.5">
              <Clock className="size-4" />
              <span>5 min read</span>
            </div>
          </div>
        </header>

        <Separator className="mb-8 bg-border" />

        <div
          className="prose prose-lg max-w-none text-foreground prose-headings:font-display prose-headings:font-bold prose-headings:tracking-wide prose-h2:mb-4 prose-h2:mt-8 prose-h2:text-3xl prose-h3:mb-3 prose-h3:mt-6 prose-h3:text-2xl prose-p:mb-4 prose-p:leading-[1.6] prose-a:text-accent prose-a:no-underline hover:prose-a:underline prose-strong:font-semibold prose-ul:my-4 prose-li:mb-2"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {post.categories && post.categories.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-muted-foreground">Categories:</span>
            {post.categories.map((category, i) => (
              <Link key={category.id} to={category.link || "#"}>
                <span
                  data-index={i}
                  className="inline-flex items-center rounded-md bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground transition-colors duration-[var(--duration-fast)] hover:bg-secondary-hover"
                >
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-muted-foreground">Tags:</span>
            {post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"}>
                <span
                  data-index={i}
                  className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-all duration-[var(--duration-fast)] hover:border-primary hover:bg-primary/5"
                >
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </article>

      <section className="bg-secondary py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 font-display text-3xl font-bold tracking-wide text-foreground md:text-4xl">
            Related Posts
          </h2>
          <PostsProvider wp_query={{ per_page: 3, order: "desc", orderby: "date" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia"]}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </section>
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 animate-pulse rounded-xl bg-muted/30" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return <p className="text-muted-foreground">No related posts available.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
        const postDate = post.date
          ? new Date(post.date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "";

        return (
          <Card
            key={post.id}
            data-index={i}
            className="group overflow-hidden border border-border bg-background shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium"
          >
            {post.featuredImage && (
              <div className="overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-48 w-full object-cover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:scale-105"
                />
              </div>
            )}
            <CardContent className="space-y-3">
              {postDate && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="size-3" />
                  {postDate}
                </p>
              )}
              <h3 className="line-clamp-2 font-display text-lg font-semibold leading-[1.4] tracking-wide text-foreground">
                {post.title}
              </h3>
              {post.excerpt && (
                <div
                  className="line-clamp-3 text-sm leading-[1.6] text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              {post.link && (
                <Link to={post.link}>
                  <Button
                    variant="link"
                    className="mt-2 h-auto p-0 text-accent hover:text-accent/80"
                  >
                    Read more →
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplate() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
      <SinglePostConsumer />
    </PostsProvider>
  );
}